import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, X } from 'lucide-react';
import { Skill } from '../types';
import { localized } from '../utils';
import { BACKEND_URL } from '../config';

const CATEGORIES: Array<{ fr: string; en: string; tKey: string }> = [
  { fr: 'Compétence',       en: 'Skill',       tKey: 'skills_skill'       },
  { fr: 'Soft skill', en: 'Soft skill',  tKey: 'skills_soft_skill'  },
  { fr: 'Langage',          en: 'Language',    tKey: 'skills_language'    },
  { fr: 'Technologie',      en: 'Technology',  tKey: 'skills_technology'  },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (skill: Skill) => void;
  onDeleted: (id: number) => void;
}

export default function SkillManagerModal({ open, onClose, onCreated, onDeleted }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [allSkills, setAllSkills] = useState<(Skill)[]>([]);
  const [categoryIdx, setCategoryIdx] = useState(0);
  const [nameFr, setNameFr] = useState('');
  const [nameEn, setNameEn] = useState('');

  useEffect(() => {
    if (!open) return;
    fetch(`${BACKEND_URL}/api/skills`).then(r => r.json()).then(setAllSkills).catch(console.error);
  }, [open]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const handleAdd = async () => {
    if (!nameFr || !nameEn) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const cat = CATEGORIES[categoryIdx];
    try {
      const res = await fetch(`${BACKEND_URL}/api/skills`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: { fr: nameFr, en: nameEn }, category: { fr: cat.fr, en: cat.en } }),
      });
      if (res.ok) {
        const created = await res.json();
        setAllSkills(prev => [...prev, created]);
        onCreated(created);
        setNameFr('');
        setNameEn('');
      }
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (id: number) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await fetch(`${BACKEND_URL}/api/skills/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setAllSkills(prev => prev.filter(s => s.id !== id));
      onDeleted(id);
    } catch (err) { console.error(err); }
  };

  const grouped: Record<string, (Skill)[]> = {};
  allSkills.forEach(s => {
    const cat = localized(s.category, lang);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(s);
  });

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="glass rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-zinc-100">{t('manage_skills_roles')}</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={20} /></button>
        </div>

        <div className="flex flex-col gap-2 mb-4 p-3 bg-ink/[0.04] rounded-lg">
          <select className="glass rounded-lg border border-ink/[0.08] text-zinc-200 px-3 py-2 text-sm" value={categoryIdx} onChange={e => setCategoryIdx(+e.target.value)}>
            {CATEGORIES.map((cat, i) => (
              <option key={i} value={i}>{t(cat.tKey)}</option>
            ))}
          </select>
          <div className="flex gap-2">
            <input className="glass rounded-lg flex-1 border border-ink/[0.08] text-zinc-200 placeholder:text-zinc-500 px-3 py-2 text-sm" placeholder={t('name_fr') as string} value={nameFr} onChange={e => setNameFr(e.target.value)} />
            <input className="glass rounded-lg flex-1 border border-ink/[0.08] text-zinc-200 placeholder:text-zinc-500 px-3 py-2 text-sm" placeholder={t('name_en') as string} value={nameEn} onChange={e => setNameEn(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }} />
          </div>
          <button onClick={handleAdd} className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark text-sm flex items-center justify-center gap-1 transition-colors">
            <Plus size={16} /> {t('add_skill')}
          </button>
        </div>

        {Object.keys(grouped).length === 0 ? (
          <p className="text-sm text-zinc-500 text-center">{t('no_skills')}</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(grouped).map(([category, items]) => (
              <div key={category}>
                <h3 className="text-xs font-medium text-zinc-500 mb-1">{category}</h3>
                <div className="flex flex-wrap gap-1.5">
                  {items.sort((a, b) => localized(a.name, lang).localeCompare(localized(b.name, lang), lang)).map(skill => (
                    <span key={skill.id} className="flex items-center gap-1 bg-zinc-800/50 border border-ink/[0.08] px-2 py-1 rounded-lg text-xs text-zinc-300">
                      {localized(skill.name, lang)}
                      <span className="text-zinc-500">({skill.name[lang === 'fr' ? 'en' : 'fr'] || ''})</span>
                      <button onClick={() => handleDelete(skill.id)} className="text-red-400/70 hover:text-red-400 ml-0.5"><Trash2 size={12} /></button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
